import { API_BASE_URL } from '../config/api';

const API_URL = 'https://translation.googleapis.com/language/translate/v2';
const FALLBACK_API_URL = 'https://translate.googleapis.com/translate_a/single';

export async function translateFallback(text: string): Promise<string> {
  const response = await fetch(API_BASE_URL + "/translate", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      text: text,
    })
  });

  if (!response.ok) {
    throw new Error("Translation failed");
  }

  const data = await response.json();
  return data.translatedText;
}

export async function translateMyMemory(
  text: string,
  sourceLang: 'en' | 'pt',
  targetLang: 'en' | 'pt'
): Promise<string> {
  const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${sourceLang}|${targetLang}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('MyMemory translation failed');
  }
  const data = await response.json();
  if (data && data.responseData && data.responseData.translatedText) {
    return data.responseData.translatedText;
  }
  throw new Error('MyMemory translation missing text');
}

export async function translateText(
  text: string, 
  sourceLang: 'en' | 'pt', 
  targetLang: 'en' | 'pt'
): Promise<string> {
  console.log(text)
  const isSingleWord = text.trim().split(/\s+/).length === 1;
  const dtParam = isSingleWord ? 'dt=t&dt=bd' : 'dt=t';
  const apiUrl = `${FALLBACK_API_URL}?client=gtx&sl=${sourceLang}&tl=${targetLang}&${dtParam}&q=${encodeURIComponent(text)}`;

  try {
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      throw new Error('Translation API error');
    }

    const data = await response.json();

    console.log(data)
    // Extract translation from response
    if (data && Array.isArray(data[0])) {
      const portugueseSentences = data[0].map(item => Array.isArray(item) ? item[0] : '').filter(Boolean);
      const primaryTranslation = portugueseSentences.join(' ');

      // If single word has bilingual dictionary entries across parts of speech (data[1]), enrich with POS meanings
      if (isSingleWord && Array.isArray(data[1]) && data[1].length >= 1) {
        const posParts: string[] = [];
        const seen = new Set<string>();
        const primaryTrimmed = primaryTranslation.trim();

        // Check if the primary translation is already present in any dictionary entry
        const allDictionaryWords = data[1].flatMap((entry: any) =>
          Array.isArray(entry) && Array.isArray(entry[1]) ? entry[1] : []
        ).map((w: any) => typeof w === 'string' ? w.toLowerCase().trim() : '');
        const primaryInDict = Boolean(primaryTrimmed && allDictionaryWords.includes(primaryTrimmed.toLowerCase()));

        for (let i = 0; i < data[1].length; i++) {
          const posEntry = data[1][i];
          if (Array.isArray(posEntry) && Array.isArray(posEntry[1])) {
            const rawWords = [...posEntry[1]];
            if (i === 0 && primaryTrimmed && !primaryInDict) {
              rawWords.unshift(primaryTrimmed);
            }
            const words: string[] = rawWords
              .filter((w: unknown): w is string => typeof w === 'string' && w.trim().length > 0)
              .slice(0, 3);
            const uniqueWords = words.filter(w => {
              const lower = w.toLowerCase();
              if (seen.has(lower)) return false;
              seen.add(lower);
              return true;
            });
            if (uniqueWords.length > 0) {
              posParts.push(uniqueWords.join(', '));
            }
          }
        }

        if (posParts.length > 0) {
          return posParts.join(' / ');
        }
      }

      return primaryTranslation;
    }
    return '';
  } catch (error) {
    console.warn('Translation warning, trying backend fallback:', error);
    try {
      return await translateFallback(text);
    } catch (fallbackError) {
      console.warn('Backend fallback failed, trying MyMemory:', fallbackError);
      try {
        return await translateMyMemory(text, sourceLang, targetLang);
      } catch (myMemoryError) {
        console.warn('MyMemory fallback also failed:', myMemoryError);
        return `(${text})`;
      }
    }
  }
}

export async function translateV0(word: string): Promise<string> {
  const result = await openGoogleTranslator
    .TranslateLanguageData({
      listOfWordsToTranslate: [word],
      fromLanguage: "en",
      toLanguage: "pt",
    })
    return result[0].translation
}